import React, { useEffect, useMemo, useState } from 'react';

import { Button, Empty, Table, Tag, Tooltip } from 'antd';
import { QuestionCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import ListPageHeader from '@shared/ListPageHeader';
import InventoryTypeReclassifyRuleConceptModal from '@shared/inventory_type_reclassify_rules/InventoryTypeReclassifyRuleConceptModal';
import MerchantSelect from '@components/operator/MerchantSelect';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import {
  operatorInventoryTypeReclassifyRuleService,
  type OperatorRule,
  type OperatorRuleTier,
} from '@services/operator/inventoryTypeReclassifyRuleService';

const i18nBase = 'admin.pages.inventory_type_reclassify_rules';

/**
 * Operator (internal) read-only view of a merchant's auto-reclassify rules.
 *
 * Operator API requires `merchant_id` query param + scopes to operator.merchants
 * (api/operator/v1/inventory_type_reclassify_rules_controller.rb#resolve_merchant),
 * so this screen is wrapped in <MerchantSelect> which prompts merchant selection
 * before mounting Content.
 */
function InventoryTypeReclassifyRulesListContent() {
  const { t } = useTranslation();
  const { merchantId } = useOperatorData();
  const [rules, setRules] = useState<OperatorRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [conceptModalVisible, setConceptModalVisible] = useState(false);

  useEffect(() => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    operatorInventoryTypeReclassifyRuleService
      .index(merchantId)
      .then((res) => {
        if (!alive) return;
        if (res.success) {
          const sorted = [...res.data].sort(
            (a, b) => a.priority - b.priority || a.name.localeCompare(b.name),
          );
          setRules(sorted);
        }
      })
      .catch((err) => console.error('Failed to load reclassify rules', err))
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [merchantId]);

  const formatTierBrief = (
    tier: OperatorRuleTier,
    next: OperatorRuleTier | undefined,
  ): string => {
    const from = tier.original_expiration_date_threshold;
    const range = next
      ? t(`${i18nBase}.form.condition_range_from_to`, { from, to: next.original_expiration_date_threshold - 1 })
      : t(`${i18nBase}.form.condition_range_open_ended`, { from });
    const condition =
      tier.trigger_mechanism === 'by_days'
        ? t(`${i18nBase}.list.condition_brief_by_days`, { days: tier.days_before_expiration })
        : t(`${i18nBase}.list.condition_brief_by_ratio`, { ratio: tier.expiration_ratio });
    return `${range} → ${condition}`;
  };

  const columns = useMemo(
    () => [
      {
        title: t(`${i18nBase}.list.column_name`),
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: t(`${i18nBase}.list.column_source`),
        key: 'source',
        render: (_: unknown, r: OperatorRule) =>
          r.source_inventory_type?.name ?? r.source_inventory_type_id,
      },
      {
        title: t(`${i18nBase}.list.column_target`),
        key: 'target',
        render: (_: unknown, r: OperatorRule) =>
          r.target_inventory_type?.name ?? r.target_inventory_type_id,
      },
      {
        title: t(`${i18nBase}.list.column_tiers`),
        key: 'tiers',
        render: (_: unknown, r: OperatorRule) => {
          const sorted = [...r.tiers].sort(
            (a, b) => a.original_expiration_date_threshold - b.original_expiration_date_threshold,
          );
          return (
            <div>
              {sorted.map((tier, i) => (
                <div key={tier.id ?? i}>{formatTierBrief(tier, sorted[i + 1])}</div>
              ))}
            </div>
          );
        },
      },
      {
        title: t(`${i18nBase}.list.column_enabled`),
        key: 'enabled',
        render: (_: unknown, r: OperatorRule) => (
          <Tag color={r.enabled ? 'green' : 'default'}>{r.enabled ? 'on' : 'off'}</Tag>
        ),
      },
      {
        title: t(`${i18nBase}.list.column_last_editor`),
        key: 'last_editor',
        render: (_: unknown, r: OperatorRule) => r.owner_merchant_user?.name ?? '-',
      },
    ],
    [t],
  );

  return (
    <div>
      <ListPageHeader
        items={['stocks', 'stocks.inventory_type_reclassify_rules']}
        guideButton={
          <Tooltip title={t('concepts.inventory_type_reclassify_rules.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
      />
      {!loading && rules.length === 0 ? (
        <Empty
          description={t('admin.pages.inventory_type_reclassify_rules.list.empty_state')}
        />
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rules}
          columns={columns}
          pagination={false}
        />
      )}
      <InventoryTypeReclassifyRuleConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  );
}

export default function OperatorInventoryTypeReclassifyRulesList() {
  return (
    <MerchantSelect>
      <InventoryTypeReclassifyRulesListContent />
    </MerchantSelect>
  );
}
